import React, { useState, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/Components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/Components/ui/tabs';
import { Button } from '@/Components/ui/button';
import { Badge } from '@/Components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/Components/ui/select';
import { Textarea } from '@/Components/ui/textarea';
import {
    Mail,
    Wrench,
    Users,
    Loader2,
    CheckCircle,
    LogOut
} from 'lucide-react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import SEO from '../Components/SEO.jsx';
import AdminCustomerDetails from '../Components/admin/AdminCustomerDetails.jsx';

const statusColors = {
    ricevuto: 'bg-blue-100 text-blue-800',
    diagnosi: 'bg-yellow-100 text-yellow-800',
    riparazione: 'bg-orange-100 text-orange-800',
    attesa_ricambi: 'bg-purple-100 text-purple-800',
    completato: 'bg-green-100 text-green-800',
    consegnato: 'bg-gray-100 text-gray-800'
};

const requestStatusColors = {
    nuovo: 'bg-blue-100 text-blue-800',
    contattato: 'bg-yellow-100 text-yellow-800',
    preventivo_inviato: 'bg-green-100 text-green-800',
    chiuso: 'bg-gray-100 text-gray-800'
};

export default function AdminDashboard() {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedRiparazione, setSelectedRiparazione] = useState(null);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [nuovoStato, setNuovoStato] = useState('');
    const [noteStato, setNoteStato] = useState('');

    const queryClient = useQueryClient();

    useEffect(() => {
        checkAuth();
    }, []);

    const checkAuth = async () => {
        try {
            const currentUser = await base44.auth.me();

            // SECURITY FIX: If we are the hardcoded admin but the DB doesn't know it,
            // try to update the DB metadata so RLS policies work.
            if (currentUser.email === 'admin@tecnosys-ups.com' && currentUser.user_metadata?.role !== 'admin') {
                console.log('Attempting to elevate user role to admin in DB...');
                try {
                    const { error } = await base44.auth.updateUser({ data: { role: 'admin' } });

                    if (!error) {
                        console.log('Role elevated successfully. Refreshing...');
                        window.location.reload();
                        return;
                    } else {
                        console.error('Failed to auto-elevate role:', error);
                        // Continue anyway, as we have client-side override
                    }
                } catch (updateError) {
                    console.error('Exception during role elevation:', updateError);
                    // Continue anyway
                }
            }

            if (currentUser.role !== 'admin') {
                window.location.href = '/';
                return;
            }
            setUser(currentUser);
        } catch (err) {
            base44.auth.redirectToLogin();
        } finally {
            setLoading(false);
        }
    };

    const { data: richiestePreventivo = [] } = useQuery({
        queryKey: ['adminRichiestePreventivo'],
        queryFn: () => base44.entities.RichiestaPreventivo.list('-created_at'),
        enabled: !!user
    });

    const { data: riparazioni = [] } = useQuery({
        queryKey: ['adminRiparazioni'],
        queryFn: () => base44.entities.Riparazione.list('-created_at'),
        enabled: !!user
    });

    const { data: utenti = [] } = useQuery({
        queryKey: ['adminUtenti'],
        queryFn: () => base44.entities.Utente.list('-created_at'),
        enabled: !!user
    });

    const updateRequestMutation = useMutation({
        mutationFn: ({ id, stato }) => base44.entities.RichiestaPreventivo.update(id, { stato }),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['adminRichiestePreventivo'] })
    });

    const updateRiparazioneMutation = useMutation({
        mutationFn: async ({ riparazioneId, stato, note }) => {
            await base44.entities.Riparazione.update(riparazioneId, { stato_corrente: stato });
            await base44.entities.StatoRiparazione.create({
                riparazione_id: riparazioneId,
                stato,
                note,
                operatore: user.email
            });

            // Invia notifica al cliente
            const riparazione = riparazioni.find(r => r.id === riparazioneId);
            if (riparazione) {
                await base44.entities.Notifica.create({
                    cliente_email: riparazione.cliente_email,
                    tipo: 'riparazione_aggiornata',
                    titolo: `Riparazione aggiornata: ${stato}`,
                    messaggio: `Lo stato della tua riparazione è stato aggiornato a: ${stato}. ${note || ''}`,
                    riparazione_id: riparazioneId
                });

                // Invia email
                await base44.integrations.Core.SendEmail({
                    to: riparazione.cliente_email,
                    subject: `Aggiornamento Riparazione UPS - ${stato}`,
                    body: `
            <h2>Aggiornamento Riparazione</h2>
            <p>Lo stato della tua riparazione è stato aggiornato:</p>
            <p><strong>Nuovo stato:</strong> ${stato}</p>
            ${note ? `<p><strong>Note:</strong> ${note}</p>` : ''}
            <p>Accedi alla tua area clienti per maggiori dettagli.</p>
          `
                });
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['adminRiparazioni'] });
            setSelectedRiparazione(null);
            setNuovoStato('');
            setNoteStato('');
        }
    });

    const handleLogout = async () => {
        await base44.auth.logout();
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 pt-20">
            <SEO title="Dashboard Admin | Tecnosys" />

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="flex items-center justify-between mb-8">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">Dashboard Admin</h1>
                        <p className="text-gray-600">Benvenuto, {user?.full_name}</p>
                    </div>
                    <Button variant="outline" onClick={handleLogout} className="gap-2">
                        <LogOut className="w-4 h-4" />
                        Logout
                    </Button>
                </div>

                {/* Stats */}
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                    <Card>
                        <CardContent className="p-6">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                                    <Mail className="w-6 h-6 text-blue-600" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold">{richiestePreventivo.filter(r => r.stato === 'nuovo').length}</p>
                                    <p className="text-sm text-gray-600">Richieste Nuove</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardContent className="p-6">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                                    <Wrench className="w-6 h-6 text-orange-600" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold">{riparazioni.filter(r => !['completato', 'consegnato'].includes(r.stato_corrente)).length}</p>
                                    <p className="text-sm text-gray-600">Riparazioni Attive</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardContent className="p-6">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                                    <CheckCircle className="w-6 h-6 text-green-600" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold">{riparazioni.filter(r => r.stato_corrente === 'completato').length}</p>
                                    <p className="text-sm text-gray-600">Completate</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardContent className="p-6">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center">
                                    <Users className="w-6 h-6 text-indigo-600" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold">{utenti.length}</p>
                                    <p className="text-sm text-gray-600">Clienti Totali</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                <Tabs defaultValue="richieste" className="space-y-6">
                    <TabsList className="bg-muted p-1 rounded-lg flex flex-wrap h-auto justify-start gap-1">
                        <TabsTrigger value="richieste" className="flex-1 min-w-[150px]">Richieste Preventivo</TabsTrigger>
                        <TabsTrigger value="riparazioni" className="flex-1 min-w-[150px]">Gestione Riparazioni</TabsTrigger>
                        <TabsTrigger value="clienti" className="flex-1 min-w-[150px]">Anagrafica Clienti</TabsTrigger>
                    </TabsList>

                    {/* Richieste Preventivo */}
                    <TabsContent value="richieste">
                        <Card>
                            <CardHeader>
                                <CardTitle>Richieste di Preventivo</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-4">
                                    {richiestePreventivo.map((richiesta) => (
                                        <div key={richiesta.id} className="border rounded-lg p-4 hover:border-blue-300 transition-colors">
                                            <div className="flex items-start justify-between">
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-3 mb-2">
                                                        <h3 className="font-semibold text-gray-900">{richiesta.nome}</h3>
                                                        <Badge className={requestStatusColors[richiesta.stato]}>{richiesta.stato}</Badge>
                                                    </div>
                                                    <div className="grid sm:grid-cols-2 gap-2 text-sm text-gray-600">
                                                        <p><strong>Email:</strong> {richiesta.email}</p>
                                                        <p><strong>Telefono:</strong> {richiesta.telefono || 'N/A'}</p>
                                                        <p><strong>Tipo:</strong> {richiesta.tipo_richiesta}</p>
                                                        <p><strong>Data:</strong> {format(new Date(richiesta.created_at), 'dd/MM/yyyy HH:mm', { locale: it })}</p>
                                                    </div>
                                                    {richiesta.messaggio && (
                                                        <p className="mt-2 text-gray-700"><strong>Messaggio:</strong> {richiesta.messaggio}</p>
                                                    )}
                                                </div>
                                                <Select
                                                    value={richiesta.stato}
                                                    onValueChange={(value) => updateRequestMutation.mutate({ id: richiesta.id, stato: value })}
                                                >
                                                    <SelectTrigger className="w-48">
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="nuovo">Nuovo</SelectItem>
                                                        <SelectItem value="contattato">Contattato</SelectItem>
                                                        <SelectItem value="preventivo_inviato">Preventivo Inviato</SelectItem>
                                                        <SelectItem value="chiuso">Chiuso</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Riparazioni */}
                    <TabsContent value="riparazioni">
                        <Card>
                            <CardHeader>
                                <CardTitle>Gestione Riparazioni</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-4">
                                    {riparazioni.map((riparazione) => (
                                        <div key={riparazione.id} className="border rounded-lg p-4">
                                            <div className="flex items-start justify-between mb-4">
                                                <div>
                                                    <div className="flex items-center gap-3 mb-2">
                                                        <h3 className="font-semibold text-gray-900">Riparazione #{riparazione.id.slice(0, 8)}</h3>
                                                        <Badge className={statusColors[riparazione.stato_corrente]}>{riparazione.stato_corrente}</Badge>
                                                    </div>
                                                    <div className="text-sm text-gray-600">
                                                        <p><strong>Cliente:</strong> {riparazione.cliente_email}</p>
                                                        <p><strong>UPS ID:</strong> {riparazione.ups_id}</p>
                                                        <p><strong>Problema:</strong> {riparazione.descrizione_problema}</p>
                                                    </div>
                                                </div>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => setSelectedRiparazione(riparazione.id)}
                                                >
                                                    Aggiorna Stato
                                                </Button>
                                            </div>

                                            {selectedRiparazione === riparazione.id && (
                                                <div className="border-t pt-4 space-y-3">
                                                    <Select value={nuovoStato} onValueChange={setNuovoStato}>
                                                        <SelectTrigger>
                                                            <SelectValue placeholder="Seleziona nuovo stato" />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value="ricevuto">Ricevuto</SelectItem>
                                                            <SelectItem value="diagnosi">In Diagnosi</SelectItem>
                                                            <SelectItem value="riparazione">In Riparazione</SelectItem>
                                                            <SelectItem value="attesa_ricambi">Attesa Ricambi</SelectItem>
                                                            <SelectItem value="completato">Completato</SelectItem>
                                                            <SelectItem value="consegnato">Consegnato</SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                    <Textarea
                                                        placeholder="Note aggiuntive..."
                                                        value={noteStato}
                                                        onChange={(e) => setNoteStato(e.target.value)}
                                                        rows={3}
                                                    />
                                                    <div className="flex gap-2">
                                                        <Button
                                                            onClick={() => updateRiparazioneMutation.mutate({
                                                                riparazioneId: riparazione.id,
                                                                stato: nuovoStato,
                                                                note: noteStato
                                                            })}
                                                            disabled={!nuovoStato || updateRiparazioneMutation.isPending}
                                                        >
                                                            {updateRiparazioneMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Conferma'}
                                                        </Button>
                                                        <Button variant="outline" onClick={() => setSelectedRiparazione(null)}>
                                                            Annulla
                                                        </Button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Clienti */}
                    <TabsContent value="clienti">
                        <Card>
                            <CardHeader>
                                <CardTitle>Anagrafica Clienti</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="rounded-md border overflow-x-auto">
                                    <table className="w-full text-sm text-left">
                                        <thead className="bg-gray-50 text-gray-700 font-medium border-b">
                                            <tr>
                                                <th className="p-4">Nome</th>
                                                <th className="p-4">Email</th>
                                                <th className="p-4">Azienda</th>
                                                <th className="p-4">Telefono</th>
                                                <th className="p-4">Registrato il</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y">
                                            {utenti.map((u) => (
                                                <tr
                                                    key={u.id}
                                                    className="hover:bg-gray-50 cursor-pointer"
                                                    onClick={() => setSelectedCustomer(u)}
                                                >
                                                    <td className="p-4 font-medium text-gray-900">{u.full_name || '-'}</td>
                                                    <td className="p-4 text-gray-600">{u.email}</td>
                                                    <td className="p-4 text-gray-600">{u.company || '-'}</td>
                                                    <td className="p-4 text-gray-600">{u.phone || '-'}</td>
                                                    <td className="p-4 text-gray-600">
                                                        {u.created_at ? format(new Date(u.created_at), 'dd/MM/yyyy HH:mm', { locale: it }) : '-'}
                                                    </td>
                                                </tr>
                                            ))}
                                            {utenti.length === 0 && (
                                                <tr>
                                                    <td colSpan="5" className="p-8 text-center text-gray-500">
                                                        Nessun cliente trovato.
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>
            </div>

            <AdminCustomerDetails
                customer={selectedCustomer}
                open={!!selectedCustomer}
                onOpenChange={(open) => !open && setSelectedCustomer(null)}
            />
        </div>
    );
}